import styled from "@emotion/styled"
import { htmlFormatTags, Value } from "@stringtale/react"
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { useOrganizatorRoleFromContext } from "app/core/providers/UserInfoProvider"
import AvatarBase from "app/files/components/Avatar"
import useFilePathCallback from "app/files/hooks/useFilePathCallback"
import { usePlay } from "app/radio/components/PlayerContext"
import { playingStateAtom } from "app/radio/state"
import { trpc } from "app/trpc"
import { useSetAtom } from "jotai"
import Link from "next/link"
import { useRouter } from "app/core/hooks/useRouter"
import { useEffect, useMemo } from "react"
import { DARK_BLUE_COLOR, PX16, PX18, PX28 } from "theme/consts"
import Button from "ui/Button"
import Center from "ui/Center"
import Group from "ui/Group"
import Grow from "ui/Grow"
import Stack from "ui/Stack"

import RoundedButton from "ui/RoundedButton"
import Title from "ui/Title"
import { groupCompare } from "../utils/groupCompare"

const Root = styled.div`
  padding: 150px 8px;
  @media (min-width: 900px) {
    padding: 150px 42px;
  }
`

export const Content = styled(Group)<{ itemCount: number }>`
  max-width: 1416px;
  width: 100%;
  box-sizing: border-box;

  flex-wrap: wrap;
  align-items: stretch;
  margin: auto;
  z-index: 1;
  position: relative;
  justify-content: center;

  gap: 16px;

  justify-content: ${(p) => (p.itemCount < 2 ? "center" : "flex-start")};

  @media (min-width: 900px) {
    gap: 24px;
    justify-content: ${(p) => (p.itemCount < 4 ? "center" : "flex-start")};
  }
`

export const GroupCard = styled.button`
  cursor: pointer;
  border: none;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  padding: 0;
  -webkit-appearance: none;
  -webkit-border-radius: 0;

  width: 100%;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0px 2px 10px #0000001a;
  :active,
  :hover {
    background-color: transparent;
  }

  width: calc(50% - 8px);

  @media (min-width: 900px) {
    width: calc(25% - 21px);
  }
`

export const GroupImage = styled.img`
  width: 100%;
  border-radius: 12px 12px 0 0;
  object-fit: cover;
  ::before {
    float: left;
    padding-top: 56.25%;
    content: "";
  }

  ::after {
    display: block;
    content: "";
    clear: both;
  }
`

const GroupImagePlacholder = styled.div`
  background: #707070;
  border-radius: 12px 12px 0 0;
  width: 100%;

  ::before {
    float: left;
    padding-top: 56.25%;
    content: "";
  }

  ::after {
    display: block;
    content: "";
    clear: both;
  }
`

export const GroupName = styled.span`
  font-size: ${PX18};
  font-weight: 600;
  color: ${DARK_BLUE_COLOR};
  line-height: 24px;
`

export const CardContent = styled(Grow)`
  background-color: white;
  border-radius: 0 0 12px 12px;
  text-align: left;
  flex-direction: column;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
`

const Avatar = styled(AvatarBase)``

export const TeachersRow = styled(Group)`
  margin-top: -46px;
  margin-bottom: 10px;
  ${Avatar}:not(:first-child) {
    margin-left: -8px;
  }
`

const TeacherIcon = styled.img`
  width: 40px;
  height: 40px;
  border: 2px solid white;
  background-color: #707070;
  border-radius: 24px;
`

const Skip = styled(Center)`
  font-size: ${PX16};
  font-weight: 600;
  color: ${DARK_BLUE_COLOR};
  text-align: center;
  margin-top: 50px;
`

const GroupPicker = ({
  onChange,
  resetErrorBoundary,
}: {
  onChange: () => Promise<void>
  resetErrorBoundary?: () => void
}) => {
  const getFilePath = useFilePathCallback()
  const { data: groups, ...groupsRes } = useSuspenseQuery(
    trpc.auth.getGroupsInSchool.queryOptions({})
  )
  const { data: organizationRole } = useSuspenseQuery({
    ...trpc.auth.getOrganizatorRole.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const setGroupForSessionMutation = useMutation(
    trpc.auth.setGroupForSession.mutationOptions()
  )
  const router = useRouter()
  const setPlayingState = useSetAtom(playingStateAtom)
  const { pause } = usePlay()
  const queryClient = useQueryClient()
  const sortedGroups = useMemo(() => {
    const sortedGroups = groups ? [...groups].sort(groupCompare) : []
    return sortedGroups
  }, [groups])

  useEffect(() => {
    async function fetch() {
      if (
        !(groupsRes.isFetching || groupsRes.isPending) &&
        groups.length <= 1
      ) {
        if (
          organizationRole &&
          (organizationRole === "MEMBER")
        ) {
          if (groups.length === 1) {
            let result = await setGroupForSessionMutation.mutateAsync({
              groupId: groups[0].id,
            })
            await queryClient.resetQueries()
            if (result) {
              await onChange()
            } else {
              await router.push(
                `/kopieer-lijsten${router.query.next ? `?next=${encodeURIComponent(router.query.next as string)}` : ""}`
              )
            }
          } else if (groups.length === 0) {
            if (router.query.next && typeof router.query.next === "string") {
              await router.push(decodeURIComponent(router.query.next))
            } else {
              await router.push("/")
            }
          }
        }
      }
    }
    fetch()
  }, [
    groups,
    groups.length,
    groupsRes.isFetching,
    groupsRes.isPending,
    onChange,
    organizationRole,
    router,
    setGroupForSessionMutation,
    queryClient,
  ])

  return (
    <>
      <Root>
        <Stack align="center">
          <Title size={PX28} style={{ marginBottom: 40 }}>
            <Value name="apps.web.src.groups.components.grouppicker.kies_je_groep">
              Kies je groep:
            </Value>
          </Title>

          {sortedGroups.length > 0 ? (
            <Content itemCount={sortedGroups.length}>
              {sortedGroups.map((group) => {
                return (
                  <GroupCard
                    style={{ flexWrap: "wrap", cursor: "pointer" }}
                    key={group.id}
                    onClick={async () => {
                      let result = await setGroupForSessionMutation.mutateAsync(
                        {
                          groupId: group.id,
                        }
                      )
                      pause()
                      setPlayingState({ type: "NONE" })
                      if (result) {
                        await queryClient.invalidateQueries()
                        await queryClient.refetchQueries({
                          queryKey: trpc.users.getCurrentGroup.queryKey(),
                        })
                        await onChange()
                      } else {
                        await queryClient.invalidateQueries()
                        await queryClient.refetchQueries({
                          queryKey: trpc.users.getCurrentGroup.queryKey(),
                        })
                        await router.push(
                          `/kopieer-lijsten${router.query.next ? `?next=${encodeURIComponent(router.query.next as string)}` : ""}`
                        )
                      }
                    }}
                    aria-label={group.displayTitle}
                  >
                    <GroupImage
                      src={
                        group.image
                          ? getFilePath(group.image, {
                              width: 326,
                              height: 183,
                              fit: "cover",
                            })
                          : "/images/placeholder-groep.png"
                      }
                      alt={`${group.displayTitle}`}
                    />
                    <CardContent>
                      <TeachersRow>
                        {group.organizationUsers.map((ou, index) => {
                          return (
                            <Avatar
                              key={index}
                              user={ou.user}
                              width={40}
                              height={40}
                              withBorder={true}
                              borderColor="white"
                              style={{
                                zIndex: group.organizationUsers.length - index,
                              }}
                            />
                          )
                        })}
                      </TeachersRow>
                      <GroupName>{group.displayTitle}</GroupName>
                    </CardContent>
                  </GroupCard>
                )
              })}
            </Content>
          ) : (
            <Stack gap="40px">
              <Content itemCount={0}>
                <span style={{ textAlign: "center" }}>
                  <Value
                    format={{
                      ...htmlFormatTags,
                    }}
                    name="apps.web.src.groups.components.grouppicker.er_zijn_geen_groep_en_aan_jou_gekoppeld"
                  >
                    {`Er zijn geen groep(en) aan jou gekoppeld.{br}Klopt dit niet?
                  Neem contact op met je beheerder.`}
                  </Value>
                </span>
              </Content>

              <RoundedButton
                variant="filled"
                onClick={async () => {
                  if (
                    router.query.next &&
                    typeof router.query.next === "string"
                  ) {
                    await router.push(decodeURIComponent(router.query.next))
                  } else {
                    await router.push("/")
                  }
                }}
              >
                <Value name="apps.web.src.groups.components.grouppicker.verder_naar_leeromgeving">
                  Verder naar leeromgeving
                </Value>
              </RoundedButton>
            </Stack>
          )}

          {organizationRole && organizationRole !== "MEMBER" ? (
            <Skip>
              <b>
                <Value name="apps.web.src.groups.components.grouppicker.schoolbeheer">
                  Schoolbeheer
                </Value>
              </b>
              <Button
                as={Link}
                style={{ textDecoration: "underline" }}
                // @ts-ignore
                href="/schoolbeheer"
                onClick={() => {
                  resetErrorBoundary?.()
                }}
              >
                <Value name="apps.web.src.groups.components.grouppicker.administratie_van_groepen_collega_s_en_schoollijsten">
                  Administratie van groepen, collega’s en schoollijsten.
                </Value>
              </Button>
            </Skip>
          ) : null}
        </Stack>
      </Root>
    </>
  )
}

export default GroupPicker
